import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { publicPlans, subscriptionPlanName, type PlanLike } from '@/lib/plans';

/**
 * What to call a member's plan on screen.
 *
 * `planName` is COPIED onto a subscription when it is created, so every row made before the
 * 26 September rename still carries an internal code — two live subscriptions still read
 * "3Mo 8Cls", and their members saw that on their own dashboard. This resolves the plan by id
 * first, exactly as the Users page does, and falls back to the stored name and then to a plan
 * described from its own duration and class limit.
 *
 * Display only: the stored `planName` is never rewritten.
 */
export function usePlanLabel() {
    const { plans } = useSubscriptions();
    const { i18n } = useTranslation();
    const current = useMemo(() => publicPlans((plans ?? []) as unknown as PlanLike[]), [plans]);
    return useMemo(
        () => (sub: { planId?: string; planName?: string; classLimit?: number | null; durationDays?: number } | null | undefined) =>
            sub ? subscriptionPlanName(sub, current, i18n.language) : '',
        [current, i18n.language],
    );
}
